/**
 * Build profile gating for cloud vs on-prem builds (per MOD-876 / on-prem
 * deployment design doc §7).
 *
 * Cloud is the default. The on-prem build is selected at build time via
 * `NEXT_PUBLIC_BUILD_PROFILE=onprem` and short-circuits all SaaS analytics
 * provider initialization regardless of whether keys are present —
 * belt-and-braces against accidental env-var leakage in deploy pipelines.
 *
 * Exports are plain `const` booleans rather than functions so bundlers can
 * fold `if (isOnPrem) ...` to a literal at build time and dead-code-eliminate
 * the unreachable branch (Next.js inlines `process.env.NEXT_PUBLIC_*`).
 */

export type BuildProfile = 'cloud' | 'onprem';

const VALID_PROFILES: readonly BuildProfile[] = ['cloud', 'onprem'];

const rawProfile = process.env.NEXT_PUBLIC_BUILD_PROFILE;

// Fail fast on a typo'd or unknown profile — silently coercing to 'cloud'
// would re-enable SaaS code paths in an on-prem build pipeline. The throw is
// hit at module load (Next.js inlines NEXT_PUBLIC_*), so build-time env-var
// errors surface during `next build`, not at runtime in the customer's
// hospital network.
if (rawProfile !== undefined && !VALID_PROFILES.includes(rawProfile as BuildProfile)) {
  throw new Error(
    `[buildProfile] Invalid NEXT_PUBLIC_BUILD_PROFILE: ${JSON.stringify(rawProfile)}. ` +
      `Expected one of: ${VALID_PROFILES.join(', ')}.`
  );
}

// Unset is also a footgun: an on-prem operator who forgets to set the env
// var gets a cloud build with all SaaS analytics enabled. Surface the
// implicit default loudly so it shows up in build logs — cloud pipelines
// should set NEXT_PUBLIC_BUILD_PROFILE=cloud explicitly to silence it.
if (rawProfile === undefined) {
  // eslint-disable-next-line no-console
  console.warn(
    '[buildProfile] NEXT_PUBLIC_BUILD_PROFILE is unset; defaulting to ' +
      "'cloud'. Set explicitly in your build pipeline " +
      '(NEXT_PUBLIC_BUILD_PROFILE=cloud or =onprem).'
  );
}

export const BUILD_PROFILE: BuildProfile = rawProfile === 'onprem' ? 'onprem' : 'cloud';

export const isOnPrem: boolean = BUILD_PROFILE === 'onprem';

/**
 * Build-time tag from `NEXT_PUBLIC_DISTRIBUTION_TAG`. `'dictate'` selects the
 * dictate bundle's first-run UX; anything else (incl. unset) = regular. Plain
 * `const` so the bundler dead-code-eliminates the branch in regular builds.
 * Build identity only — feature code gates on the resolved value (see
 * distributionTagAtoms). docs/desktop/dictation/DICTATE-BUNDLE.md.
 */
export const isDictateBundle: boolean =
  process.env.NEXT_PUBLIC_DISTRIBUTION_TAG === 'dictate';

/**
 * Authentication provider selection (MOD-885 / on-prem auth workstream).
 *
 * Cloud uses Kinde; on-prem uses Keycloak. Defaults follow the build profile,
 * but `NEXT_PUBLIC_AUTH_PROVIDER` can override explicitly — useful when an
 * engineer is testing the on-prem auth path against a local Keycloak while
 * still running a `cloud`-profile dev build.
 *
 * Like `isOnPrem`, these are plain `const` so the bundler can dead-code-
 * eliminate the unreachable branch at build time (Next.js inlines
 * `process.env.NEXT_PUBLIC_*`).
 */
export type AuthProvider = 'kinde' | 'keycloak';

const VALID_AUTH_PROVIDERS: readonly AuthProvider[] = ['kinde', 'keycloak'];

const rawAuthProvider = process.env.NEXT_PUBLIC_AUTH_PROVIDER;

if (
  rawAuthProvider !== undefined &&
  !VALID_AUTH_PROVIDERS.includes(rawAuthProvider as AuthProvider)
) {
  throw new Error(
    `[buildProfile] Invalid NEXT_PUBLIC_AUTH_PROVIDER: ${JSON.stringify(rawAuthProvider)}. ` +
      `Expected one of: ${VALID_AUTH_PROVIDERS.join(', ')}.`
  );
}

const AUTH_PROVIDER_DEFAULT: AuthProvider = isOnPrem ? 'keycloak' : 'kinde';

export const AUTH_PROVIDER: AuthProvider =
  rawAuthProvider === 'keycloak' || rawAuthProvider === 'kinde'
    ? rawAuthProvider
    : AUTH_PROVIDER_DEFAULT;

// Surface a deliberate override loudly. The default behaviour is a safe
// cascade from `BUILD_PROFILE`; an explicit override that disagrees with
// the default is almost always either an engineer doing a one-off debug
// run, or a misconfigured deployment that just silently disabled Keycloak
// on an on-prem build. We can't tell which, so we shout. Cloud build
// pipelines that intentionally pin `NEXT_PUBLIC_AUTH_PROVIDER=kinde`
// (matches default) get no warning.
if (rawAuthProvider !== undefined && rawAuthProvider !== AUTH_PROVIDER_DEFAULT) {
  // eslint-disable-next-line no-console
  console.warn(
    `[buildProfile] NEXT_PUBLIC_AUTH_PROVIDER override (${JSON.stringify(
      rawAuthProvider
    )}) differs from the build-profile default (${JSON.stringify(
      AUTH_PROVIDER_DEFAULT
    )}). Ensure this is intentional — on-prem deployments must use 'keycloak'.`
  );
}

export const isKeycloak: boolean = AUTH_PROVIDER === 'keycloak';

/**
 * **Do NOT use `isKinde` as the sole gate for Kinde SDK initialization on
 * on-prem builds.** Per `onprem-guardrails.md` F1, `kinde-auth-nextjs`
 * and `kinde-auth-react` are forbidden on on-prem code paths. Because
 * `NEXT_PUBLIC_AUTH_PROVIDER=kinde` is permitted on an on-prem build as a
 * debug-only override, `isKinde` can be `true` even when `isOnPrem` is
 * also `true`. Gate Kinde SDK init on `!isOnPrem` instead (or
 * `isKinde && !isOnPrem` if both signals are needed) so dead-code
 * elimination in the on-prem bundle is preserved.
 */
export const isKinde: boolean = AUTH_PROVIDER === 'kinde';

/**
 * SaaS telemetry suppression (capability-layering telemetry axis, design D1).
 *
 * Whether the build initialises phone-home SaaS SDKs — PostHog, Amplitude,
 * Segment/Braze, Datadog RUM/logs, Intercom, GTM, Firebase, Snowplow's SaaS
 * collector. Defaults follow the build profile, but `NEXT_PUBLIC_SAAS_TELEMETRY`
 * can override, so a full-surface cloud build can run silent against a local
 * stack without becoming an on-prem build.
 *
 * This is a different question from `isOnPrem`, which answers "is this the
 * hermetic build" and still governs feature surface, endpoints, copy and
 * on-prem behaviour differences.
 */
export type SaasTelemetry = 'on' | 'off';

const VALID_SAAS_TELEMETRY: readonly SaasTelemetry[] = ['on', 'off'];

const rawSaasTelemetry = process.env.NEXT_PUBLIC_SAAS_TELEMETRY;

if (
  rawSaasTelemetry !== undefined &&
  !VALID_SAAS_TELEMETRY.includes(rawSaasTelemetry as SaasTelemetry)
) {
  throw new Error(
    `[buildProfile] Invalid NEXT_PUBLIC_SAAS_TELEMETRY: ${JSON.stringify(rawSaasTelemetry)}. ` +
      `Expected one of: ${VALID_SAAS_TELEMETRY.join(', ')}.`
  );
}

const SAAS_TELEMETRY_DEFAULT: SaasTelemetry = isOnPrem ? 'off' : 'on';

// Unlike the AUTH_PROVIDER override — which warns, because pointing a cloud
// dev build at Keycloak is a documented debug posture — an on-prem build with
// telemetry on has no legitimate case: it would re-enable SaaS egress on the
// hermetic build that F1's belt-and-braces exists to prevent. A warning in a
// build log is not a sufficient barrier for that contract, so this throws at
// module load and fails `next build` rather than at runtime in a hospital
// network — the same fail-fast rationale as the BUILD_PROFILE validation above.
if (isOnPrem && rawSaasTelemetry === 'on') {
  throw new Error(
    '[buildProfile] NEXT_PUBLIC_SAAS_TELEMETRY=on is forbidden on an on-prem ' +
      'build (NEXT_PUBLIC_BUILD_PROFILE=onprem): it would re-enable SaaS ' +
      'telemetry egress on a hermetic build. Unset it, or set it to ' +
      "'off' to match the build-profile default."
  );
}

export const SAAS_TELEMETRY: SaasTelemetry =
  rawSaasTelemetry === 'on' || rawSaasTelemetry === 'off'
    ? rawSaasTelemetry
    : SAAS_TELEMETRY_DEFAULT;

// Surface the divergent override loudly. Given the throw above, the only
// combination that reaches here is cloud + 'off' — the local dev/e2e profile.
// It is intentional, but a cloud pipeline that sets it by accident ships with
// every SaaS SDK silently dark, so it must show in build logs.
if (rawSaasTelemetry !== undefined && rawSaasTelemetry !== SAAS_TELEMETRY_DEFAULT) {
  // eslint-disable-next-line no-console
  console.warn(
    `[buildProfile] NEXT_PUBLIC_SAAS_TELEMETRY override (${JSON.stringify(
      rawSaasTelemetry
    )}) differs from the build-profile default (${JSON.stringify(
      SAAS_TELEMETRY_DEFAULT
    )}). SaaS SDK initialisation is disabled on this cloud build — ensure this is intentional.`
  );
}

/**
 * The gate surface for SaaS SDK init and telemetry emission. Gate sites branch
 * on this plain const, never on a capability object's property (design D2):
 * Next.js inlines `process.env.NEXT_PUBLIC_*` and bundlers fold branches over
 * plain consts, but no minifier can prove an object property immutable across
 * modules — routing gates through one would forfeit dead-code elimination
 * permanently, not just until scope hoisting lands (ARCFE-664).
 */
export const isSaasTelemetryEnabled: boolean = SAAS_TELEMETRY === 'on';
